import { PaneProps } from '@fullstory/components/legacy/Pane';
import { intl } from '@fullstory/intl';
import { ObjectType } from '@fullstory/proto/fs/services/dx-objects';
import { LibraryObjectType } from '@fullstory/routes';
import React, { useEffect, useState } from 'react';
import { DetailViewElementPane } from '../../containers';
import { CollapsibleHeader } from '../CollapsibleHeader';

export type DxObjectFilterHeaderProps = PaneProps & {
  objectType: LibraryObjectType;
  /**
   * Whether the filter header is expanded. This prop will be propegated down to the
   * internal collapsible header component, but is completely optional.
   *
   * Only use when external state is managing the collapse state.
   */
  expanded?: boolean;
  children: React.ReactNode;
  /**
   * React node rendered to the left of the filter button
   */
  leftItem?: React.ReactNode;

  /**
   * React node rendered to the right of the filter button
   */
  rightItem?: React.ReactNode;
  /**
   * Callback function to handle the toggle event
   */
  onToggle?: (expanded: boolean) => void;
};

export const DxObjectFilterHeader = ({
  objectType,
  children,
  leftItem,
  rightItem,
  expanded = true,
  onToggle,
  ...rest
}: DxObjectFilterHeaderProps) => {
  const [isCollapsed, setIsCollapsed] = useState(!expanded);

  const handleToggle = (val: boolean) => {
    setIsCollapsed(!val);
    onToggle?.(val);
  };

  // allows use of external state to control collapse
  useEffect(() => {
    setIsCollapsed(!expanded);
  }, [expanded]);

  return (
    <DetailViewElementPane
      margin={
        objectType === ObjectType.SEGMENT ||
        objectType === ObjectType.RETENTION ||
        objectType === ObjectType.JOURNEY ||
        objectType === ObjectType.DASHBOARD
          ? undefined
          : [0, 24, 16, 24]
      }
      marginBottom={objectType === ObjectType.RETENTION ? 16 : undefined}
      collapsable
      expanded={!isCollapsed}
      border="standard"
      header={
        <CollapsibleHeader
          titleText={intl.formatMessage(
            {
              defaultMessage:
                '{objectType, select, 1 {Metric} 2 {Funnel} 3 {Retention} 5 {Segment} 6 {Dashboard} 7 {Heatmap} 8 {Journey} other {unknown type}} Filters',
              id: 'OHKnGW',
            },
            { objectType },
          )}
          toggleText={intl.formatMessage({
            defaultMessage: 'Filters',
            id: 'zSOvI0',
          })}
          isCollapsed={isCollapsed}
          leftItem={leftItem}
          rightItem={rightItem}
          onToggle={handleToggle}
        />
      }
      {...rest}
    >
      {children}
    </DetailViewElementPane>
  );
};
